import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Button, Table } from 'antd';
import { InfiniteScroll, Spin } from 'common/components';
import { showMessage, showSuccessMessage } from 'common/helpers';
import { LIST_LIMIT_0 } from 'common/config';
import { Bin, Plus } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { IContract } from 'entities/Contract/Contract.models';
import { UserAddModal } from 'entities/User/components';
import { IUser } from 'entities/User/User.models';
import { renderContractGeneralSettingsUserListRecords } from 'entities/Settings/Settings.helper';
import { EUserRole } from 'entities/User/User.enum';
import {
  createContractUser,
  deleteContractUser,
  getContractUsers,
  selectContractUsers,
  setContractUsers,
} from 'entities/Contract/Contract.slice';
import { getUserList, selectUserList } from 'entities/User/User.slice';

interface IComponentProps {
  isEditable?: boolean;
  header: ReactNode;
  contract: IContract | null;
}

export const SettingsContractGeneralTab: FC<IComponentProps> = ({ isEditable = true, header, contract }) => {
  const dispatch = useAppDispatch();
  // contract
  const contractUsers = useAppSelector(selectContractUsers);
  // user
  const userList = useAppSelector(selectUserList);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [openUserAddModal, setOpenUserAddModal] = useState<boolean>(false);

  const contractSellerManagerListIds = contractUsers.map((user) => user.id);
  const sellerManagerList = userList.filter((user) => !contractSellerManagerListIds.includes(user.id));

  const onOpenUserAddModalClick = () => {
    dispatch(
      getUserList({
        limit: LIST_LIMIT_0,
        role: EUserRole.SellerManager,
        onSuccess: () => setOpenUserAddModal(true),
      }),
    );
  };

  const onAddContractManagerClick = (value: IUser, onSuccess: () => void) => {
    if (contract) {
      dispatch(
        createContractUser({
          contractId: contract.id,
          userId: value.id,
          onSuccess: () => {
            onSuccess();
            showSuccessMessage(`Новый менеджер добавлен для контракта «${contract.buyerName}».`);
          },
        }),
      );
    }
  };

  const onDeleteContractManagerClick = (id: number) => {
    if (contract) {
      dispatch(
        deleteContractUser({
          contractId: contract.id,
          userId: id,
          onSuccess: () => {
            showMessage({
              icon: <Bin className="icon-bin-dark-grey" />,
              content: `Менеджер удален из контракта «${contract.buyerName}».`,
            });
          },
        }),
      );
    }
  };

  useEffect(() => {
    const fetch = async () => {
      setFetchLoading(true);

      if (contract?.id) {
        dispatch(setContractUsers([]));
        await dispatch(getContractUsers({ contractId: contract.id }));
      }

      setFetchLoading(false);
    };

    fetch();
  }, [contract?.id]);

  return (
    <>
      <InfiniteScroll>
        {header}

        <div className="text-h4 mb-3">Менеджер по контракту</div>

        <div className="text-tag color-dark-grey mb-6" style={{ maxWidth: 800 }}>
          Менеджер может обрабатывать входящие заявки, добавлять товары в корзины клиентов и управлять настройками контракта.
          Количество менеджеров контракта не ограничено.
        </div>

        {isEditable && (
          <div className="mb-6">
            <Button className="button-sm primary" icon={<Plus className="icon-plus-white" />} onClick={onOpenUserAddModalClick}>
              Добавить менеджера
            </Button>
          </div>
        )}

        {!!contractUsers?.length && (
          <Table
            className="mb-19 contract-general-settings__table"
            showHeader={false}
            pagination={false}
            size="middle"
            dataSource={contractUsers.map((user) => ({ ...user, key: user.id }))}
            columns={renderContractGeneralSettingsUserListRecords(isEditable, onDeleteContractManagerClick)}
          />
        )}

        {fetchLoading && (
          <div className="relative flex align-center justify-center">
            <Spin />
          </div>
        )}
      </InfiniteScroll>

      <UserAddModal
        open={openUserAddModal}
        placeholder="Поиск по сотрудникам"
        loading={false}
        userList={sellerManagerList}
        onClose={() => setOpenUserAddModal(false)}
        onAdd={onAddContractManagerClick}
      />
    </>
  );
};
